import type {
  FilterOption,
  ProvaFacilFilters,
  ProvaFacilSnapshot,
  ProvaFacilSummary,
  RankedItem
} from "../../types/provaFacil";

const ALLOWED_TIPO_CODES = new Set(["001", "002", "003", "004"]);
const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sab", "Dom"];

export const EMPTY_FILTERS: ProvaFacilFilters = {
  tipo: "",
  polo: "",
  period: "",
  course: ""
};

export function tipoCode(label: string): string {
  const match = String(label || "").trim().match(/^(\d{1,3})/);
  return match ? match[1].padStart(3, "0") : "";
}

export function isAllowedTipo(label: string): boolean {
  return ALLOWED_TIPO_CODES.has(tipoCode(label));
}

export function semesterFromDay(day: string): string {
  const date = isoDate(day);
  const month = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear();
  if (month >= 3 && month <= 8) return `${year}-S1`;
  if (month >= 9 && month <= 12) return `${year}-S2`;
  return `${year - 1}-S2`;
}

export function periodLabel(value: string): string {
  const [year, semester] = String(value).split("-S");
  if (!year || !semester) return value;
  return `${year}.${semester}`;
}

export function weekdayLabel(index: number): string {
  return WEEKDAYS[index] || "-";
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("pt-BR").format(value);
}

export function formatPercent(value: number): string {
  return `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}

export function formatShortDate(value: string | null): string {
  if (!value) return "-";
  const date = isoDate(value);
  return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
}

export function averageDuration(summary: ProvaFacilSummary): number {
  return summary.durationCount ? summary.durationSum / summary.durationCount : 0;
}

export function buildOptions(snapshot: ProvaFacilSnapshot): Record<keyof ProvaFacilFilters, FilterOption[]> {
  const periods = new Set<string>();
  for (const row of snapshot.cubes.day || []) {
    const [day, , , tipoId, , , , count] = row;
    const tipoLabel = snapshot.dimensions.tipos[tipoId] || "";
    if (count > 0 && isAllowedTipo(tipoLabel)) periods.add(semesterFromDay(day));
  }

  return {
    tipo: snapshot.dimensions.tipos
      .filter(isAllowedTipo)
      .map((label) => ({ label, value: label })),
    polo: snapshot.dimensions.polos.map((label) => ({ label, value: label })),
    period: [...periods].sort().map((value) => ({ label: periodLabel(value), value })),
    course: snapshot.dimensions.cursos.map((label) => ({ label, value: label }))
  };
}

export function buildSummary(snapshot: ProvaFacilSnapshot, filters: ProvaFacilFilters): ProvaFacilSummary {
  const poloId = selectedId(snapshot.dimensions.polos, filters.polo);
  const tipoId = selectedId(snapshot.dimensions.tipos, filters.tipo);
  const courseId = selectedId(snapshot.dimensions.cursos, filters.course);

  const hours = new Array<number>(24).fill(0);
  const weekdays = new Array<number>(7).fill(0);
  const poloCounts = new Map<string, number>();
  const tipoCounts = new Map<string, number>();
  const courseCounts = new Map<string, number>();
  const periods = new Map<string, number>();
  let total = 0;
  let generatedTotal = 0;
  let durationSum = 0;
  let durationCount = 0;
  let min: string | null = null;
  let max: string | null = null;

  for (const row of snapshot.cubes.day || []) {
    const [day, p, status, tipo, course, , , count, rowDurationSum = 0, rowDurationCount = 0] = row;
    const tipoLabel = snapshot.dimensions.tipos[tipo] || "";
    if (!isAllowedTipo(tipoLabel)) continue;
    if (!matchesFilter(p, poloId)) continue;
    if (!matchesFilter(tipo, tipoId)) continue;
    if (!matchesFilter(course, courseId)) continue;
    if (filters.period && semesterFromDay(day) !== filters.period) continue;

    total += Number(count || 0);
    durationSum += Number(rowDurationSum || 0);
    durationCount += Number(rowDurationCount || 0);

    const statusLabel = snapshot.dimensions.statuses[status] || "";
    if (normalizeKey(statusLabel).includes("gerado")) generatedTotal += Number(count || 0);

    if (!min || day < min) min = day;
    if (!max || day > max) max = day;

    increment(poloCounts, snapshot.dimensions.polos[p] || "Polo nao informado", count);
    increment(tipoCounts, tipoLabel || "Tipo nao informado", count);
    increment(courseCounts, snapshot.dimensions.cursos[course] || "Curso nao informado", count);
    increment(periods, semesterFromDay(day), count);
    const weekday = (isoDate(day).getUTCDay() + 6) % 7;
    weekdays[weekday] += Number(count || 0);
  }

  for (const row of snapshot.cubes.hourDay || []) {
    const [day, hour, p, , tipo, course, , , count] = row;
    if (!isAllowedTipo(snapshot.dimensions.tipos[tipo] || "")) continue;
    if (!matchesFilter(p, poloId)) continue;
    if (!matchesFilter(tipo, tipoId)) continue;
    if (!matchesFilter(course, courseId)) continue;
    if (filters.period && semesterFromDay(day) !== filters.period) continue;
    if (hour >= 0 && hour < 24) hours[hour] += Number(count || 0);
  }

  const unique = new Set<string>();
  for (const row of snapshot.cubes.presentUnique || []) {
    const [day, p, , tipo, course, , ra] = row;
    if (!isAllowedTipo(snapshot.dimensions.tipos[tipo] || "")) continue;
    if (!matchesFilter(p, poloId)) continue;
    if (!matchesFilter(tipo, tipoId)) continue;
    if (!matchesFilter(course, courseId)) continue;
    if (filters.period && semesterFromDay(day) !== filters.period) continue;
    unique.add(`${day}:${ra}`);
  }

  return {
    total,
    generatedTotal,
    presentUniqueCount: unique.size,
    durationSum,
    durationCount,
    periods: rank(periods),
    hours,
    weekdays,
    poloCounts: rank(poloCounts),
    tipoCounts: rank(tipoCounts),
    courseCounts: rank(courseCounts),
    range: { min, max }
  };
}

export function top(items: RankedItem[], limit = 8): RankedItem[] {
  return [...items].sort((a, b) => b.value - a.value).slice(0, limit);
}

export function bottom(items: RankedItem[], limit = 8): RankedItem[] {
  return [...items].filter((item) => item.value > 0).sort((a, b) => a.value - b.value).slice(0, limit);
}

function selectedId(values: string[], selected: string): number {
  if (!selected) return -1;
  return values.findIndex((value) => value === selected);
}

function matchesFilter(actual: number, selected: number): boolean {
  return selected < 0 || actual === selected;
}

function increment(map: Map<string, number>, label: string, value: number): void {
  map.set(label, (map.get(label) || 0) + Number(value || 0));
}

function rank(map: Map<string, number>): RankedItem[] {
  return [...map.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

function normalizeKey(value: string): string {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function isoDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}
